import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";

import type { AmountLimitCell, AmountLimitRow, EffectiveAmountLimit } from "../../types/paymentLimits.types";

/** The live rows that decide the cell in the CURRENT scope (org view falls back to the global rows). */
export function rowsInScope(cell: AmountLimitCell, scopeOrgId: string | null): AmountLimitRow[] {
  if (scopeOrgId && cell.organization.length > 0) return cell.organization;
  return cell.global;
}

/**
 * The row an editor should seed from for one payer kind: the kind's own row when it exists, else the
 * both-kinds row — the same preference the backend's resolution applies within a scope level.
 */
export function rowFor(rows: AmountLimitRow[], payerType: LimitPayerType | null): AmountLimitRow | null {
  if (payerType) {
    const own = rows.find((row) => row.payer_type === payerType);
    if (own) return own;
  }
  return rows.find((row) => row.payer_type === null) ?? null;
}

export function effectiveFor(cell: AmountLimitCell, payerType: LimitPayerType): EffectiveAmountLimit | null {
  return cell.effective.find((limit) => limit.payer_type === payerType) ?? null;
}

/** True in org view when the organization holds at least one row of its own on this corridor. */
export function hasOwnOverride(cell: AmountLimitCell, scopeOrgId: string | null): boolean {
  return scopeOrgId !== null && cell.organization.length > 0;
}

export type LimitSourceKey =
  | "source_default"
  | "source_global_setting"
  | "source_global"
  | "source_org_override";

/** Which i18n line explains where the effective bounds of a cell come from, in the current scope. */
export function sourceKeyFor(cell: AmountLimitCell, scopeOrgId: string | null): LimitSourceKey {
  const bounded = cell.effective.some((limit) => limit.min_amount !== null || limit.max_amount !== null);
  if (!bounded) return "source_default";
  if (hasOwnOverride(cell, scopeOrgId)) return "source_org_override";
  return scopeOrgId ? "source_global" : "source_global_setting";
}

/** A bound for a table cell: the amount at the currency precision, or the caller's "none" label. */
export function formatBound(value: string | null, currency: string, locale: string, none: string): string {
  return value === null ? none : formatCurrencyAmount(value, currency, locale);
}
